import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Grid, ShoppingCart, Heart, User } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import { useCart } from '../context/CartContext';
import { isCustomerAuthenticated } from '../utils/customerAuth';
import { useAuthGate } from '../context/AuthGateContext';
import { savePendingAuthAction } from '../utils/pendingAuthAction';

const OfferBannerPopupHost = lazy(() => import('./offerPopup/OfferBannerPopupHost'));

interface MainLayoutProps {
  children: React.ReactNode;
  hideHeader?: boolean;
  hideFooter?: boolean;
  hideBottomNav?: boolean;
}

const MainLayout: React.FC<MainLayoutProps> = ({
  children,
  hideHeader = false,
  hideFooter = false,
  hideBottomNav = false
}) => {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);
  const { cartItems } = useCart();
  const { openAuthModal } = useAuthGate();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const navItems = [
    { path: '/', icon: Home, label: 'Home' },
    { path: '/categories', icon: Grid, label: 'Categories' },
    { path: '/cart', icon: ShoppingCart, label: 'Cart', badge: cartCount },
    { path: '/wishlist', icon: Heart, label: 'Wishlist' },
    { path: '/profile', icon: User, label: 'Account' }
  ];

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/' || location.pathname === '/home';
    }
    return location.pathname.startsWith(path);
  };

  const protectedNavPaths = ['/cart', '/wishlist', '/profile', '/checkout', '/orders', '/addresses'];

  const handleNavClick = (e: React.MouseEvent, path: string) => {
    if (!protectedNavPaths.includes(path) || isCustomerAuthenticated()) return;
    e.preventDefault();
    savePendingAuthAction({ type: 'navigate', path });
    openAuthModal({ returnPath: path });
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {!hideHeader && <Header cartItemCount={cartCount} />}
      
      <main className={`flex-grow ${!hideBottomNav && isMobile ? 'pb-20' : ''}`}>
        {children}
      </main>
      
      {!hideFooter && <Footer />}

      <Suspense fallback={null}>
        <OfferBannerPopupHost />
      </Suspense>
      
      {/* Mobile Bottom Navigation */}
      {!hideBottomNav && isMobile && (
        <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg safe-area-bottom">
          <div className="flex items-center justify-around h-16">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={(e) => handleNavClick(e, item.path)}
                  className={`flex flex-col items-center justify-center flex-1 h-full relative transition-colors ${
                    active ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <div className="relative">
                    <Icon className="w-6 h-6" strokeWidth={active ? 2.5 : 2} />
                    {(item.badge || 0) > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                        {(item.badge || 0) > 99 ? '99+' : item.badge}
                      </span>
                    )}
                  </div>
                  <span className="text-xs mt-0.5 font-medium">{item.label}</span>
                  {active && (
                    <span className="absolute bottom-1 w-10 h-0.5 bg-blue-600 rounded-full" />
                  )}
                </Link>
              );
            })}
          </div>
        </nav>
      )}
    </div>
  );
};

export default MainLayout;
